import React, {useState} from 'react';
import {Line, LineConfig} from '@ant-design/charts';
import {LineChartData} from '../types/LineChartData';
import {getFormattedNumber, getMonthNumber} from '../utils/NumberUtils';
import {Modal} from 'react-bootstrap';
import {G2} from '@ant-design/plots';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowUpRightFromSquare} from '@fortawesome/free-solid-svg-icons';
import {Point} from '../types/Point';

interface LineChartProps {
    title: string;
    data: LineChartData[];
    reduce?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({title, data, reduce}) => {
    const [show, setShow] = useState(false);

    const height = show ? 600 : reduce ? 250 : 300;
    const width = show ? 1200 : reduce ? 480 : 700;

    const currentMonth = new Date().getMonth() + 1;

    const uniqueTypes: string[] = [];
    data.map(item => {
        if (uniqueTypes.indexOf(item.dataType) === -1) {
            uniqueTypes.push(item.dataType);
        }
    });

    G2.registerShape('point', 'breath-point', {
        draw(cfg, container) {
            // @ts-ignore
            const data: LineChartData = cfg.data;
            // @ts-ignore
            const point: Point = {x: cfg.x, y: cfg.y};
            const group = container.addGroup();
            const highlightColor = '#c51c1c'

            if (getMonthNumber(data.timestamp) === currentMonth) {
                const decorator1 = group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 5,
                        fill: highlightColor,
                        opacity: 0.5,
                    },
                });
                const decorator2 = group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 5,
                        fill: highlightColor,
                        opacity: 0.5,
                    },
                });
                const decorator3 = group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 5,
                        fill: highlightColor,
                        opacity: 0.5,
                    },
                });
                const decorator4 = group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 5,
                        fill: highlightColor,
                        opacity: 0.5,
                    },
                });
                decorator1.animate(
                    {
                        r: 15,
                        opacity: 0,
                    },
                    {
                        duration: 3000,
                        easing: 'easeLinear',
                        repeat: true,
                    },
                );
                decorator2.animate(
                    {
                        r: 15,
                        opacity: 0,
                    },
                    {
                        duration: 3000,
                        easing: 'easeLinear',
                        repeat: true,
                        delay: 1000,
                    },
                );
                decorator3.animate(
                    {
                        r: 15,
                        opacity: 0,
                    },
                    {
                        duration: 3000,
                        easing: 'easeLinear',
                        repeat: true,
                        delay: 2000,
                    },
                );
                decorator4.animate(
                    {
                        r: 15,
                        opacity: 0,
                    },
                    {
                        duration: 3000,
                        easing: 'easeLinear',
                        repeat: true,
                        delay: 3000,
                    },
                );
                group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 6,
                        fill: highlightColor,
                        opacity: 1,
                    },
                });
            } else {
                group.addShape('circle', {
                    attrs: {
                        x: point.x,
                        y: point.y,
                        r: 4,
                        fill: cfg.color,
                    },
                });
            }

            return group;
        },
    });

    const config: LineConfig = {
        data,
        height: height,
        width: width,
        xField: 'timestamp',
        yField: 'value',
        xAxis: {
            tickLine: {
                style: {
                    lineWidth: 0,
                    stroke: '#aaa',
                },
            },
            subTickLine: {
                style: {
                    lineWidth: 2,
                    stroke: '#aaa',
                },
                length: 5,
                count: 1,
            },
        },
        yAxis: {
            line: {
                style: {
                    stroke: '#aaa',
                },
            },
            label: {
                // 数值格式化为千分位
                formatter: v => `${v}`.replace(/\d{1,3}(?=(\d{3})+$)/g, s => `${s},`),
            },
            title: {
                text: 'tis. Kč',
                style: {
                    fontWeight: 'bold',
                    fontSize: 16,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: '#ddd',
                    },
                },
                alternateColor: 'rgba(0,0,0,0.05)',
            },
        },
        tooltip: {
            //@ts-ignore
            shared: false,
            customContent: (title, items) => {
                if (items.length == 0) {
                    return '';
                }
                const {value, color} = items[0];
                const label = '(' + getFormattedNumber(value) + ' tis. Kč)';
                return (
                    <div>
                        <span
                            style={{
                                display: 'inline-flex',
                                flex: 1,
                                justifyContent: 'space-between',
                                marginTop: 10,
                                marginBottom: 10,
                            }}
                        >
                            <div
                                style={{
                                    width: 10,
                                    height: 10,
                                    marginRight: 10,
                                    backgroundColor: color,
                                }}
                            ></div>
                            <span style={{marginRight: 16}}>
                                <strong>{title}</strong>
                            </span>
                            <span className="g2-tooltip-list-item-value">{label}</span>
                        </span>
                    </div>
                );
            },
        },
        point: {
            shape: 'breath-point',
        },
        seriesField: 'dataType',
        color: ({dataType}) => {
            if (uniqueTypes[0] === dataType) {
                return '#2c4291';
            }
            if (uniqueTypes[1] === dataType) {
                return '#4edeec';
            }
            return '#ffda26';
        },
        legend: {
            layout: 'horizontal',
            position: 'bottom',
        },
    };

    return (
        <div>
            {show ? (
                <Modal fullscreen show={show} onHide={() => setShow(false)} centered>
                    <Modal.Header className="centered" closeButton>
                        <h2>{title}</h2>
                    </Modal.Header>
                    <Modal.Body style={{display: 'flex', justifyContent: 'center', flexDirection: 'column', padding: 100}}>
                        <div className="centered">
                            <Line {...config} />
                        </div>
                    </Modal.Body>
                </Modal>
            ) : (
                <div>
                    <Line {...config} />
                    <div className="class-col">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="touchable" onClick={() => setShow(true)} />
                    </div>
                </div>
            )}
        </div>
    );
};
